// frontend/src/components/widgets/MultiZoneWidget.js
// ✅ FIXED: Verwendet jetzt öffentliche API-Endpoints mit organizationId
import React, { useState, useEffect } from 'react';
import WeatherWidget from './WeatherWidget';
import ClockWidget from './ClockWidget';
import RSSWidget from './RSSWidget';
import StreamingWidget from './StreamingWidget';
import WebContentWidget from './WebContentWidget';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch';

// Widget Registry für Zone Content
const WIDGET_COMPONENTS = {
  'weather-widget': WeatherWidget,
  'clock-widget': ClockWidget,
  'rss-feed-widget': RSSWidget,
  'streaming-widget': StreamingWidget,
  'web-content-widget': WebContentWidget
};

// ✅ FIXED: organizationId als prop hinzugefügt
const MultiZoneWidget = ({ config = {}, screenSize = {}, organizationId }) => {
  const [zoneContents, setZoneContents] = useState({});
  const [playlists, setPlaylists] = useState({});
  const [currentIndices, setCurrentIndices] = useState({});
  const [loading, setLoading] = useState(true);

  const { layoutType, zones = [], backgroundColor, gapSize } = config;
  
  // 📱 Responsive Multipliers
  const { 
    fontSizeMultiplier = 1, 
    spacingMultiplier = 1,
    width,
    height 
  } = screenSize || {};
  
  // Responsive Gap Size
  const responsiveGap = Math.round((gapSize || 4) * spacingMultiplier);

  // Layout Grid Configurations
  const LAYOUT_GRIDS = {
    '2-horizontal': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr'
    },
    '2-vertical': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: '1fr 1fr'
    },
    '3-vertical': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: '1fr 1fr 1fr'
    },
    '3-horizontal': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gridTemplateRows: '1fr'
    },
    '3-main-side': {
      display: 'grid',
      gridTemplateColumns: '3fr 1fr',
      gridTemplateRows: '1fr 1fr',
      gridTemplateAreas: '"main side1" "main side2"'
    },
    '4-grid': {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gridTemplateRows: '1fr 1fr'
    },
    '4-main-corners': {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr 1fr',
      gridTemplateRows: '1fr 2fr 1fr',
      gridTemplateAreas: '"corner1 . corner2" ". main ." "corner3 . corner4"'
    },
    '2-news-ticker': {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gridTemplateRows: '85fr 15fr'
    },
    '4-sidebar-ticker': {
      display: 'grid',
      gridTemplateColumns: '1fr 4fr',
      gridTemplateRows: '7fr 1.5fr 1.5fr',
      gridTemplateAreas: '"sidebar main" "sidebar ticker" "sidebar weather"'
    }
  };

  // ✅ FIXED: Load content mit organizationId dependency
  useEffect(() => {
    if (organizationId && zones.length > 0) {
      loadZoneContents();
    } else if (!organizationId) {
      console.warn('⚠️ MultiZoneWidget: organizationId not provided, content may not load correctly');
      // Fallback: Versuche mit Token (für Dashboard Preview)
      loadZoneContentsWithToken();
    }
  }, [zones, organizationId]);

  // Rotation handler for playlists
  useEffect(() => {
    const intervals = {};

    Object.keys(playlists).forEach(zoneId => {
      const zone = zones.find(z => z.zoneId === zoneId);
      if (!zone) return;

      const playlist = playlists[zoneId];
      if (playlist && playlist.length > 1) {
        intervals[zoneId] = setInterval(() => {
          setCurrentIndices(prev => ({
            ...prev,
            [zoneId]: ((prev[zoneId] || 0) + 1) % playlist.length
          }));
        }, (zone.duration || 30) * 1000);
      }
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [playlists, zones]);

  // ✅ NEW: Public API Loading (für Player)
  const loadZoneContents = async () => {
    console.log('🔄 Loading MultiZone contents with PUBLIC API for org:', organizationId);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const contents = {};
      const playlistData = {};
      const indices = {};

      for (const zone of zones) {
        const { zoneId, contentType, contentId } = zone;
        console.log(`📍 Loading zone ${zoneId}: type=${contentType}, id=${contentId}`);

        if (contentType === 'playlist') {
          // ✅ PUBLIC ENDPOINT - keine Auth erforderlich
          try {
            const response = await fetch(`${API_URL}/playlists/public/${contentId}/org/${organizationId}`);
            if (response.ok) {
              const playlist = await response.json();
              playlistData[zoneId] = playlist.media_items || [];
              indices[zoneId] = 0;
              console.log(`✅ Zone ${zoneId}: Playlist loaded with ${playlistData[zoneId].length} items`);
            } else {
              console.error(`❌ Zone ${zoneId}: Failed to load playlist - ${response.status}`);
            }
          } catch (err) {
            console.error(`❌ Zone ${zoneId}: Playlist fetch error:`, err);
          }
          
        } else if (contentType === 'widget') {
          // ✅ PUBLIC ENDPOINT - keine Auth erforderlich
          try {
            const response = await fetch(`${API_URL}/features/public/instances/${organizationId}`);
            if (response.ok) {
              const featuresData = await response.json();
              
              // Find widget instance across all features
              for (const feature of featuresData.features || []) {
                const instance = feature.instances?.find(i => i.id.toString() === contentId);
                if (instance) {
                  contents[zoneId] = {
                    type: 'widget',
                    featureId: feature.id,
                    instance: instance
                  };
                  console.log(`✅ Zone ${zoneId}: Widget instance found - ${instance.name}`);
                  break;
                }
              }
              
              if (!contents[zoneId]) {
                console.warn(`⚠️ Zone ${zoneId}: Widget instance ${contentId} not found`);
              }
            } else {
              console.error(`❌ Zone ${zoneId}: Failed to load features - ${response.status}`);
            }
          } catch (err) {
            console.error(`❌ Zone ${zoneId}: Features fetch error:`, err);
          }
          
        } else if (contentType === 'media') {
          // ✅ PUBLIC ENDPOINT - existiert bereits
          try {
            const response = await fetch(`${API_URL}/media/public/${organizationId}`);
            if (response.ok) {
              const mediaData = await response.json();
              const mediaItem = mediaData.find(m => m.id.toString() === contentId);
              if (mediaItem) {
                contents[zoneId] = {
                  type: 'media',
                  item: mediaItem
                };
                console.log(`✅ Zone ${zoneId}: Media loaded - ${mediaItem.name}`);
              } else {
                console.warn(`⚠️ Zone ${zoneId}: Media ${contentId} not found`);
              }
            } else {
              console.error(`❌ Zone ${zoneId}: Failed to load media - ${response.status}`);
            }
          } catch (err) {
            console.error(`❌ Zone ${zoneId}: Media fetch error:`, err);
          }
        }
      }

      setZoneContents(contents);
      setPlaylists(playlistData);
      setCurrentIndices(indices);
      setLoading(false);
      
      console.log('✅ MultiZone contents loaded:', {
        zones: Object.keys(contents).length,
        playlists: Object.keys(playlistData).length
      });

    } catch (error) {
      console.error('❌ Error loading zone contents:', error);
      setLoading(false);
    }
  };

  // Fallback: Token-basiertes Laden (für Dashboard/Preview)
  const loadZoneContentsWithToken = async () => {
    console.log('🔄 Loading MultiZone contents with TOKEN (fallback)');

    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      const contents = {};
      const playlistData = {};
      const indices = {};

      for (const zone of zones) {
        const { zoneId, contentType, contentId } = zone;

        if (contentType === 'playlist') {
          const response = await fetch(`${API_URL}/playlists/${contentId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const playlist = await response.json();
            playlistData[zoneId] = playlist.media_items || [];
            indices[zoneId] = 0;
          }
          
        } else if (contentType === 'widget') {
          const response = await fetch(`${API_URL}/features`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const featuresData = await response.json();
            
            for (const feature of featuresData.features || []) {
              if (feature.isActivated) {
                const instancesRes = await fetch(`${API_URL}/features/${feature.id}/instances`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (instancesRes.ok) {
                  const instancesData = await instancesRes.json();
                  const instance = instancesData.instances?.find(i => i.id.toString() === contentId);
                  
                  if (instance) {
                    contents[zoneId] = {
                      type: 'widget',
                      featureId: feature.id,
                      instance: instance
                    };
                    break;
                  }
                }
              }
            }
          }
          
        } else if (contentType === 'media') {
          const response = await fetch(`${API_URL}/media`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const mediaData = await response.json();
            const mediaItem = mediaData.find(m => m.id.toString() === contentId);
            
            if (mediaItem) {
              contents[zoneId] = {
                type: 'media',
                item: mediaItem
              };
            }
          }
        }
      }

      setZoneContents(contents);
      setPlaylists(playlistData);
      setCurrentIndices(indices);
      setLoading(false);

    } catch (error) {
      console.error('Error loading zone contents (token):', error);
      setLoading(false);
    }
  };

  // Calculate zone-specific screen size based on layout
  const getZoneScreenSize = (zoneIndex) => {
    if (!screenSize) return { fontSizeMultiplier: 1, spacingMultiplier: 1 };

    const { fontSizeMultiplier = 1, spacingMultiplier = 1 } = screenSize;

    // Sidebar in 3-main-side (Zones 1 & 2 are tall/narrow)
    if (layoutType === '3-main-side' && (zoneIndex === 1 || zoneIndex === 2)) {
      return {
        ...screenSize,
        fontSizeMultiplier: fontSizeMultiplier * 0.3,
        spacingMultiplier: spacingMultiplier * 0.3,
        layoutHint: 'sidebar'
      };
    }

    // Sidebar in 4-sidebar-ticker layout (Zone 0)
    if (layoutType === '4-sidebar-ticker' && zoneIndex === 0) {
      return {
        ...screenSize,
        fontSizeMultiplier: fontSizeMultiplier * 0.4,
        spacingMultiplier: spacingMultiplier * 0.4,
        layoutHint: 'sidebar'
      };
    }

    // Ticker and Weather in 4-sidebar-ticker (Zones 2 & 3)
    if (layoutType === '4-sidebar-ticker' && (zoneIndex === 2 || zoneIndex === 3)) {
      return {
        ...screenSize,
        fontSizeMultiplier: fontSizeMultiplier * 0.35,
        spacingMultiplier: spacingMultiplier * 0.35,
        layoutHint: 'ticker'
      };
    }

    // News ticker in 2-news-ticker (Zone 1)
    if (layoutType === '2-news-ticker' && zoneIndex === 1) {
      return {
        ...screenSize,
        fontSizeMultiplier: fontSizeMultiplier * 0.3,
        spacingMultiplier: spacingMultiplier * 0.3,
        layoutHint: 'ticker'
      };
    }

    // Default: full size for main zones
    return screenSize;
  };

  const renderZoneContent = (zone) => {
    const { zoneId, contentType } = zone;
    const zoneIndex = zones.findIndex(z => z.zoneId === zoneId);
    const zoneScreenSize = getZoneScreenSize(zoneIndex);

    // Render Playlist Content
    if (contentType === 'playlist') {
      const playlist = playlists[zoneId];
      const currentIndex = currentIndices[zoneId] || 0;
      
      if (!playlist || playlist.length === 0) {
        return (
          <div 
            className="flex items-center justify-center h-full text-gray-400"
            style={{ fontSize: `${20 * zoneScreenSize.fontSizeMultiplier}px` }}
          >
            No content
          </div>
        );
      }

      const currentItem = playlist[currentIndex];
      
      if (currentItem.type === 'widget') {
        const WidgetComponent = WIDGET_COMPONENTS[currentItem.feature_id];
        if (WidgetComponent) {
          return <WidgetComponent config={currentItem.config} screenSize={zoneScreenSize} />;
        }
      } else if (currentItem.type === 'image') {
        return (
          <img
            src={currentItem.url?.startsWith('http') ? currentItem.url : `${API_BASE_URL}${currentItem.url}`}
            alt={currentItem.name}
            className="w-full h-full object-cover"
          />
        );
      } else if (currentItem.type === 'video') {
        return (
          <video
            src={currentItem.url?.startsWith('http') ? currentItem.url : `${API_BASE_URL}${currentItem.url}`}
            autoPlay
            loop
            muted
            className="w-full h-full object-cover"
          />
        );
      } else if (currentItem.type === 'webpage') {
        return (
          <iframe
            src={currentItem.url}
            className="w-full h-full border-0"
            title={currentItem.name}
          />
        );
      }
    }

    // Render Widget Content
    if (contentType === 'widget') {
      const content = zoneContents[zoneId];
      if (!content) {
        return (
          <div 
            className="flex items-center justify-center h-full text-gray-400"
            style={{ fontSize: `${20 * zoneScreenSize.fontSizeMultiplier}px` }}
          >
            Loading...
          </div>
        );
      }

      const WidgetComponent = WIDGET_COMPONENTS[content.featureId];
      if (WidgetComponent) {
        return <WidgetComponent config={content.instance.config} screenSize={zoneScreenSize} />;
      }
    }

    // Render Media Content
    if (contentType === 'media') {
      const content = zoneContents[zoneId];
      if (!content) {
        return (
          <div 
            className="flex items-center justify-center h-full text-gray-400"
            style={{ fontSize: `${20 * zoneScreenSize.fontSizeMultiplier}px` }}
          >
            Loading...
          </div>
        );
      }

      const mediaItem = content.item;
      
      if (mediaItem.type === 'image') {
        return (
          <img
            src={mediaItem.url?.startsWith('http') ? mediaItem.url : `${API_BASE_URL}${mediaItem.url}`}
            alt={mediaItem.filename}
            className="w-full h-full object-cover"
          />
        );
      } else if (mediaItem.type === 'video') {
        return (
          <video
            src={mediaItem.url?.startsWith('http') ? mediaItem.url : `${API_BASE_URL}${mediaItem.url}`}
            autoPlay
            loop
            muted
            className="w-full h-full object-cover"
          />
        );
      }
    }

    return (
      <div 
        className="flex items-center justify-center h-full text-gray-400"
        style={{ fontSize: `${20 * zoneScreenSize.fontSizeMultiplier}px` }}
      >
        Invalid content type
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div 
          className="text-white font-bold"
          style={{ fontSize: `${32 * fontSizeMultiplier}px` }}
        >
          Loading Multi-Zone Layout...
        </div>
      </div>
    );
  }

  const gridStyle = LAYOUT_GRIDS[layoutType] || LAYOUT_GRIDS['2-horizontal'];

  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        backgroundColor: backgroundColor || '#000000',
        ...gridStyle,
        gap: `${responsiveGap}px`,
        padding: `${responsiveGap}px`,
        overflow: 'hidden'
      }}
    >
      {zones.map((zone, idx) => (
        <div
          key={zone.zoneId}
          style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            backgroundColor: '#000000',
            borderRadius: `${4 * spacingMultiplier}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            gridArea: layoutType === '3-main-side' 
              ? (idx === 0 ? 'main' : `side${idx}`)
              : layoutType === '4-main-corners'
              ? (idx === 0 ? 'main' : `corner${idx}`)
              : layoutType === '4-sidebar-ticker'
              ? (idx === 0 ? 'sidebar' : idx === 1 ? 'main' : idx === 2 ? 'ticker' : 'weather')
              : 'auto'
          }}
        >
          {renderZoneContent(zone)}
        </div>
      ))}
    </div>
  );
};

export default MultiZoneWidget;